import { useState } from 'react';
import { 
  MousePointer2, 
  Hand,
  Square, 
  StickyNote, 
  Type, 
  Pencil,
  Frame,
  Eraser,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Grid3X3,
  Magnet,
  Circle,
  Diamond,
  ArrowRight,
  CornerDownRight,
  Spline,
  Hexagon,
  Cloud,
  Minus,
  Image,
  Link,
  MessageSquare,
  MessageCircle,
  Play,
  Download,
  Layout,
  Keyboard,
  X,
  Highlighter,
  Check,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  StretchHorizontal,
  StretchVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

// Color presets (shared for pen and shapes)
const colorPresets = [
  { name: 'Black', color: '#1E293B' },
  { name: 'Gray', color: '#64748B' },
  { name: 'Red', color: '#EF4444' },
  { name: 'Orange', color: '#F97316' },
  { name: 'Yellow', color: '#EAB308' },
  { name: 'Green', color: '#22C55E' },
  { name: 'Blue', color: '#2563EB' },
  { name: 'Purple', color: '#8B5CF6' },
  { name: 'Pink', color: '#EC4899' },
  { name: 'White', color: '#FFFFFF' },
];

// Pen color presets
const penColors = [
  { name: 'Black', color: '#1E293B' },
  { name: 'Gray', color: '#64748B' },
  { name: 'Red', color: '#EF4444' },
  { name: 'Orange', color: '#F97316' },
  { name: 'Yellow', color: '#EAB308' },
  { name: 'Green', color: '#22C55E' },
  { name: 'Blue', color: '#2563EB' },
  { name: 'Purple', color: '#8B5CF6' },
  { name: 'Pink', color: '#EC4899' },
];

// Pen width presets
const penWidths = [
  { name: 'Thin', width: 1 },
  { name: 'Medium', width: 2 },
  { name: 'Thick', width: 4 },
  { name: 'Bold', width: 6 },
];

// Stroke width presets for shapes
const strokeWidths = [
  { name: 'None', width: 0 },
  { name: 'Thin', width: 1 },
  { name: 'Medium', width: 2 },
  { name: 'Thick', width: 3 },
  { name: 'Bold', width: 4 },
];

// Fill opacity presets
const fillOpacities = [
  { name: 'Solid', value: 1 },
  { name: 'Semi', value: 0.5 },
  { name: 'Light', value: 0.25 },
  { name: 'None', value: 0 },
];

// Font size presets for shape text
const fontSizes = [
  { name: 'XS', size: 10 },
  { name: 'S', size: 12 },
  { name: 'M', size: 14 },
  { name: 'L', size: 18 },
  { name: 'XL', size: 24 },
];

const shapes = [
  { id: 'rectangle', icon: Square, label: 'Rectangle', shortcut: 'R' },
  { id: 'rounded', icon: Square, label: 'Rounded', shortcut: '' },
  { id: 'circle', icon: Circle, label: 'Circle', shortcut: 'O' },
  { id: 'diamond', icon: Diamond, label: 'Diamond', shortcut: 'D' },
  { id: 'hexagon', icon: Hexagon, label: 'Hexagon', shortcut: '' },
  { id: 'cloud', icon: Cloud, label: 'Cloud', shortcut: '' },
  { id: 'parallelogram', icon: Minus, label: 'Parallelogram', shortcut: '' },
];

const connectorTypes = [
  { id: 'straight', icon: ArrowRight, label: 'Straight Arrow' },
  { id: 'elbow', icon: CornerDownRight, label: 'Elbow Connector' },
  { id: 'curved', icon: Spline, label: 'Curved Arrow' },
];

const stickyColors = [
  { id: 'yellow', color: '#FEF3C7', label: 'Yellow' },
  { id: 'pink', color: '#FCE7F3', label: 'Pink' },
  { id: 'blue', color: '#DBEAFE', label: 'Blue' },
  { id: 'green', color: '#D1FAE5', label: 'Green' },
  { id: 'purple', color: '#EDE9FE', label: 'Purple' },
  { id: 'orange', color: '#FFEDD5', label: 'Orange' },
];

const keyboardShortcuts = [
  { key: 'V', action: 'Select tool' },
  { key: 'H', action: 'Hand/Pan tool' },
  { key: 'Space + Drag', action: 'Pan canvas' },
  { key: 'R', action: 'Rectangle' },
  { key: 'O', action: 'Circle/Oval' },
  { key: 'D', action: 'Diamond' },
  { key: 'S', action: 'Sticky note' },
  { key: 'T', action: 'Text' },
  { key: 'L', action: 'Line/Connector' },
  { key: 'P', action: 'Pen/Draw' },
  { key: 'F', action: 'Frame' },
  { key: 'I', action: 'Upload Image' },
  { key: 'E', action: 'Eraser' },
  { key: 'C', action: 'Comment tool' },
  { key: 'N', action: 'New shape at center' },
  { key: '1/2/3', action: 'Straight/Elbow/Curved connector' },
  { key: 'Delete', action: 'Delete selected' },
  { key: '+/-', action: 'Zoom in/out' },
  { key: '0', action: 'Reset zoom & pan' },
  { key: 'Ctrl+Z', action: 'Undo' },
  { key: 'Ctrl+Shift+Z', action: 'Redo' },
  { key: 'Ctrl+C', action: 'Copy' },
  { key: 'Ctrl+V', action: 'Paste' },
  { key: 'Ctrl+D', action: 'Duplicate' },
  { key: 'Ctrl+G', action: 'Group' },
  { key: 'Ctrl+Shift+G', action: 'Ungroup' },
  { key: 'Ctrl+L', action: 'Lock/Unlock' },
  { key: 'Ctrl+S', action: 'Save' },
  { key: 'Escape', action: 'Deselect / Exit present mode' },
  { key: 'Double-click', action: 'Edit text in shape' },
];

export const WhiteboardToolbar = ({ 
  activeTool, 
  setActiveTool,
  connectorType,
  setConnectorType,
  zoom,
  setZoom,
  gridType,
  setGridType,
  snapToGrid,
  setSnapToGrid,
  undo,
  redo,
  canUndo,
  canRedo,
  onExport,
  onPresentMode,
  onApplyTemplate,
  penSettings,
  setPenSettings,
  shapeSettings,
  setShapeSettings,
  textSettings,
  setTextSettings,
  stickySettings,
  setStickySettings,
  selectedIds,
  onAlign,
  commentCount = 0,
  onToggleCommentsPanel,
  onImageUpload
}) => {
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  const isShapeTool = activeTool?.startsWith('shape-');
  const hasMultipleSelected = selectedIds?.length > 1;
  
  return (
    <>
      {/* Alignment Toolbar - Shows when multiple objects selected */}
      {hasMultipleSelected && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 top-4 bg-white dark:bg-[#1E293B] rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-2 z-50 flex items-center gap-1"
          data-testid="alignment-toolbar"
        >
          <span className="text-xs text-gray-500 px-2">{selectedIds.length} selected</span>
          <div className="w-px h-6 bg-gray-200" />
          
          {/* Horizontal alignment */}
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'left')}
            title="Align Left"
          >
            <AlignLeft size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'center')}
            title="Align Center"
          >
            <AlignCenter size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'right')}
            title="Align Right"
          >
            <AlignRight size={16} />
          </Button>
          
          <div className="w-px h-6 bg-gray-200" />
          
          {/* Vertical alignment */}
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'top')}
            title="Align Top"
          >
            <AlignStartVertical size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'middle')}
            title="Align Middle"
          >
            <AlignCenterVertical size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
            onClick={() => onAlign(selectedIds, 'bottom')}
            title="Align Bottom"
          >
            <AlignEndVertical size={16} />
          </Button>
          
          {selectedIds?.length >= 3 && (
            <>
              <div className="w-px h-6 bg-gray-200" />
              
              {/* Distribution */}
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
                onClick={() => onAlign(selectedIds, 'distribute-h')}
                title="Distribute Horizontally"
              >
                <StretchHorizontal size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-8 h-8 p-0 text-gray-600 hover:text-blue-600"
                onClick={() => onAlign(selectedIds, 'distribute-v')}
                title="Distribute Vertically"
              >
                <StretchVertical size={16} />
              </Button>
            </>
          )}
        </div>
      )}
    
      {/* Main Toolbar - Left Side */}
      <div 
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white dark:bg-[#1E293B] rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-2 z-40 flex flex-col gap-1"
        data-testid="whiteboard-toolbar"
      >
        {/* Select Tool */}
        <Button
          variant={activeTool === 'select' ? 'default' : 'ghost'}
          size="sm"
          className={`w-10 h-10 p-0 ${
            activeTool === 'select'
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setActiveTool('select')}
          title="Select (V)"
          data-testid="tool-select"
        >
          <MousePointer2 size={20} />
        </Button>

        {/* Hand/Pan Tool */}
        <Button
          variant={activeTool === 'hand' ? 'default' : 'ghost'}
          size="sm"
          className={`w-10 h-10 p-0 ${
            activeTool === 'hand'
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setActiveTool('hand')}
          title="Hand/Pan (H) - or hold Space"
          data-testid="tool-hand"
        >
          <Hand size={20} />
        </Button>

        <div className="w-full h-px bg-gray-200 my-1" />

        {/* Shapes Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={activeTool.startsWith('shape-') ? 'default' : 'ghost'}
              size="sm"
              className={`w-10 h-10 p-0 relative ${
                activeTool.startsWith('shape-')
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Shapes (R)"
              data-testid="tool-shapes"
            >
              <Square size={20} />
              {/* Color indicator */}
              <div 
                className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-white"
                style={{ backgroundColor: shapeSettings?.fillColor || '#FFFFFF' }}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-64 p-3">
            <DropdownMenuLabel className="text-xs text-gray-500 p-0 mb-2">Shapes</DropdownMenuLabel>
            <div className="grid grid-cols-4 gap-1 mb-3">
              {shapes.map((shape) => (
                <button
                  key={shape.id}
                  onClick={() => setActiveTool(`shape-${shape.id}`)}
                  className={`p-2 rounded-md hover:bg-gray-100 flex items-center justify-center ${
                    activeTool === `shape-${shape.id}` ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
                  }`}
                  title={`${shape.label}${shape.shortcut ? ` (${shape.shortcut})` : ''}`}
                >
                  <shape.icon size={18} />
                </button>
              ))}
            </div>
            
            <DropdownMenuSeparator />
            
            {/* Fill Color */}
            <div className="mt-3">
              <DropdownMenuLabel className="text-xs text-gray-500 p-0 mb-2">Fill Color</DropdownMenuLabel>
              <div className="grid grid-cols-5 gap-1.5">
                {colorPresets.map((c) => (
                  <button
                    key={c.color}
                    className={`w-7 h-7 rounded-md border-2 transition-all ${
                      shapeSettings?.fillColor === c.color 
                        ? 'border-[#2563EB] scale-110' 
                        : 'border-gray-200 hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.color }}
                    onClick={() => setShapeSettings(prev => ({ ...prev, fillColor: c.color }))}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Stroke Color */}
            <div className="mt-3">
              <DropdownMenuLabel className="text-xs text-gray-500 p-0 mb-2">Stroke Color</DropdownMenuLabel>
              <div className="grid grid-cols-5 gap-1.5">
                {colorPresets.slice(0, -1).map((c) => (
                  <button
                    key={c.color}
                    className={`w-7 h-7 rounded-md border-2 transition-all ${
                      shapeSettings?.strokeColor === c.color 
                        ? 'border-[#2563EB] scale-110' 
                        : 'border-gray-200 hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.color }}
                    onClick={() => setShapeSettings(prev => ({ ...prev, strokeColor: c.color }))}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Stroke Width */}
            <div className="mt-3">
              <DropdownMenuLabel className="text-xs text-gray-500 p-0 mb-2">Stroke Width</DropdownMenuLabel>
              <div className="flex gap-1">
                {strokeWidths.map((w) => (
                  <button
                    key={w.width}
                    className={`flex-1 h-7 rounded-md border flex items-center justify-center transition-all text-xs ${
                      shapeSettings?.strokeWidth === w.width 
                        ? 'border-[#2563EB] bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    onClick={() => setShapeSettings(prev => ({ ...prev, strokeWidth: w.width }))}
                    title={w.name}
                  >
                    {w.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fill Opacity */}
            <div className="mt-3">
              <DropdownMenuLabel className="text-xs text-gray-500 p-0 mb-2">Fill Opacity</DropdownMenuLabel>
              <div className="flex gap-1">
                {fillOpacities.map((o) => (
                  <button
                    key={o.value}
                    className={`flex-1 h-7 rounded-md border flex items-center justify-center transition-all text-xs ${
                      shapeSettings?.fillOpacity === o.value 
                        ? 'border-[#2563EB] bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    onClick={() => setShapeSettings(prev => ({ ...prev, fillOpacity: o.value }))}
                    title={o.name}
                  >
                    {o.name}
                  </button>
                ))}
              </div>
            </div>
            
            <DropdownMenuSeparator />
            
            {/* Text Settings */}
            <div className="p-3 space-y-3">
              <div className="text-xs font-medium text-gray-500 uppercase">Text</div>
              
              {/* Font Size */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 w-10">Size</span>
                {fontSizes.map((f) => (
                  <button
                    key={f.size}
                    className={`flex-1 h-7 rounded-md border flex items-center justify-center transition-all text-xs ${
                      shapeSettings?.fontSize === f.size 
                        ? 'border-[#2563EB] bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    onClick={() => setShapeSettings(prev => ({ ...prev, fontSize: f.size }))}
                    title={`Font size ${f.size}px`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
              
              {/* Text Color */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 w-10">Color</span>
                <div className="flex gap-1 flex-wrap flex-1">
                  {colorPresets.slice(0, 5).map((c) => (
                    <button
                      key={c.color}
                      className={`w-6 h-6 rounded-md border-2 transition-all ${
                        shapeSettings?.textColor === c.color 
                          ? 'border-[#2563EB] scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: c.color }}
                      onClick={() => setShapeSettings(prev => ({ ...prev, textColor: c.color }))}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sticky Notes Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={activeTool.startsWith('sticky-') ? 'default' : 'ghost'}
              size="sm"
              className={`w-10 h-10 p-0 relative ${
                activeTool.startsWith('sticky-')
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Sticky Notes (S)"
            >
              <StickyNote size={20} />
              {/* Text color indicator */}
              <div 
                className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-white"
                style={{ backgroundColor: stickySettings?.textColor || '#1E293B' }}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56 p-3">
            <div className="space-y-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Sticky Note Colors</div>
              
              {/* Background Color */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-16">Background</span>
                <div className="flex gap-1 flex-wrap flex-1">
                  {stickyColors.map((sticky) => (
                    <button
                      key={sticky.id}
                      className={`w-6 h-6 rounded-md border-2 transition-all ${
                        activeTool === `sticky-${sticky.id}`
                          ? 'border-[#2563EB] scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: sticky.color }}
                      onClick={() => setActiveTool(`sticky-${sticky.id}`)}
                      title={sticky.label}
                    />
                  ))}
                </div>
              </div>
              
              {/* Text Color */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-16">Text Color</span>
                <div className="flex gap-1 flex-wrap flex-1">
                  {colorPresets.map((c) => (
                    <button
                      key={c.color}
                      className={`w-6 h-6 rounded-md border-2 transition-all ${
                        stickySettings?.textColor === c.color 
                          ? 'border-[#2563EB] scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: c.color }}
                      onClick={() => setStickySettings(prev => ({ ...prev, textColor: c.color }))}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Text Tool with Settings */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={activeTool === 'text' ? 'default' : 'ghost'}
              size="sm"
              className={`w-10 h-10 p-0 relative ${
                activeTool === 'text'
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Text (T)"
              data-testid="tool-text"
            >
              <Type size={20} />
              {/* Color indicator */}
              <div 
                className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-white"
                style={{ backgroundColor: textSettings?.textColor || '#1E293B' }}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56 p-3">
            <div className="space-y-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Text Settings</div>
              
              {/* Font Size */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-12">Size</span>
                <div className="flex gap-1 flex-1">
                  {[12, 14, 16, 20, 24, 32].map((size) => (
                    <button
                      key={size}
                      className={`px-2 py-1 text-xs rounded ${
                        textSettings?.fontSize === size
                          ? 'bg-[#2563EB] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setTextSettings(prev => ({ ...prev, fontSize: size }));
                        setActiveTool('text');
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Text Color */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-12">Color</span>
                <div className="flex gap-1 flex-wrap flex-1">
                  {colorPresets.map((c) => (
                    <button
                      key={c.color}
                      className={`w-6 h-6 rounded-md border-2 transition-all ${
                        textSettings?.textColor === c.color 
                          ? 'border-[#2563EB] scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: c.color }}
                      onClick={() => {
                        setTextSettings(prev => ({ ...prev, textColor: c.color }));
                        setActiveTool('text');
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
              
              {/* Font Weight */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-12">Style</span>
                <div className="flex gap-1">
                  <button
                    className={`px-3 py-1 text-xs rounded ${
                      textSettings?.fontWeight === 'normal'
                        ? 'bg-[#2563EB] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      setTextSettings(prev => ({ ...prev, fontWeight: 'normal' }));
                      setActiveTool('text');
                    }}
                  >
                    Normal
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded font-bold ${
                      textSettings?.fontWeight === 'bold'
                        ? 'bg-[#2563EB] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      setTextSettings(prev => ({ ...prev, fontWeight: 'bold' }));
                      setActiveTool('text');
                    }}
                  >
                    Bold
                  </button>
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="w-full h-px bg-gray-200 my-1" />

        {/* Connector Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={activeTool === 'connector' ? 'default' : 'ghost'}
              size="sm"
              className={`w-10 h-10 p-0 ${
                activeTool === 'connector'
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Connector (L)"
              data-testid="tool-connector"
            >
              {connectorType === 'elbow' ? <CornerDownRight size={20} /> : 
               connectorType === 'curved' ? <Spline size={20} /> : 
               <ArrowRight size={20} />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-48">
            {connectorTypes.map((type) => (
              <DropdownMenuItem
                key={type.id}
                onClick={() => {
                  setConnectorType(type.id);
                  setActiveTool('connector');
                }}
                className={`flex items-center gap-2 ${connectorType === type.id ? 'bg-blue-50' : ''}`}
              >
                <type.icon size={16} />
                {type.label}
                {connectorType === type.id && (
                  <span className="ml-auto text-[#2563EB]">✓</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Pen Tool with Settings */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={activeTool === 'pen' ? 'default' : 'ghost'}
              size="sm"
              className={`w-10 h-10 p-0 relative ${
                activeTool === 'pen'
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              onClick={(e) => {
                if (activeTool !== 'pen') {
                  e.preventDefault();
                  setActiveTool('pen');
                }
              }}
              title="Pen (P) - Click to activate, click again for options"
              data-testid="tool-pen"
            >
              {penSettings?.isHighlighter ? <Highlighter size={20} /> : <Pencil size={20} />}
              {/* Color indicator dot */}
              <div 
                className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-white"
                style={{ backgroundColor: penSettings?.color || '#1E293B' }}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56 p-3">
            <div className="space-y-4">
              {/* Mode toggle */}
              <div>
                <DropdownMenuLabel className="text-xs font-medium text-[#64748B] p-0 mb-2">Mode</DropdownMenuLabel>
                <div className="flex gap-2">
                  <Button
                    variant={!penSettings?.isHighlighter ? 'default' : 'outline'}
                    size="sm"
                    className={`flex-1 ${!penSettings?.isHighlighter ? 'bg-[#2563EB]' : ''}`}
                    onClick={() => setPenSettings(prev => ({ ...prev, isHighlighter: false }))}
                  >
                    <Pencil size={14} className="mr-1" />
                    Pen
                  </Button>
                  <Button
                    variant={penSettings?.isHighlighter ? 'default' : 'outline'}
                    size="sm"
                    className={`flex-1 ${penSettings?.isHighlighter ? 'bg-[#EAB308]' : ''}`}
                    onClick={() => setPenSettings(prev => ({ ...prev, isHighlighter: true }))}
                  >
                    <Highlighter size={14} className="mr-1" />
                    Highlight
                  </Button>
                </div>
              </div>
              
              {/* Color picker */}
              <div>
                <DropdownMenuLabel className="text-xs font-medium text-[#64748B] p-0 mb-2">Color</DropdownMenuLabel>
                <div className="grid grid-cols-5 gap-1.5">
                  {penColors.map((c) => (
                    <button
                      key={c.color}
                      className={`w-7 h-7 rounded-md border-2 transition-all ${
                        penSettings?.color === c.color 
                          ? 'border-[#2563EB] scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: c.color }}
                      onClick={() => setPenSettings(prev => ({ ...prev, color: c.color }))}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
              
              {/* Stroke width */}
              <div>
                <DropdownMenuLabel className="text-xs font-medium text-[#64748B] p-0 mb-2">Stroke Width</DropdownMenuLabel>
                <div className="flex gap-2">
                  {penWidths.map((w) => (
                    <button
                      key={w.width}
                      className={`flex-1 h-8 rounded-md border flex items-center justify-center transition-all ${
                        penSettings?.width === w.width 
                          ? 'border-[#2563EB] bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setPenSettings(prev => ({ ...prev, width: w.width }))}
                      title={w.name}
                    >
                      <div 
                        className="rounded-full bg-current"
                        style={{ 
                          width: `${w.width * 3}px`, 
                          height: `${w.width * 3}px`,
                          backgroundColor: penSettings?.color || '#1E293B'
                        }}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-1 text-center">
                  {penWidths.find(w => w.width === penSettings?.width)?.name || 'Medium'}
                </p>
              </div>
              
              <DropdownMenuSeparator />
              
              {/* Pressure sensitivity toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#1E293B]">Speed-based width</p>
                  <p className="text-[10px] text-[#94A3B8]">Thin when fast, thick when slow</p>
                </div>
                <button
                  className={`w-10 h-5 rounded-full transition-colors ${
                    penSettings?.pressureSensitive ? 'bg-[#2563EB]' : 'bg-gray-200'
                  }`}
                  onClick={() => setPenSettings(prev => ({ ...prev, pressureSensitive: !prev.pressureSensitive }))}
                >
                  <div 
                    className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      penSettings?.pressureSensitive ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Frame Tool */}
        <Button
          variant={activeTool === 'frame' ? 'default' : 'ghost'}
          size="sm"
          className={`w-10 h-10 p-0 ${
            activeTool === 'frame'
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setActiveTool('frame')}
          title="Frame (F)"
          data-testid="tool-frame"
        >
          <Frame size={20} />
        </Button>

        {/* Image Upload Tool */}
        <Button
          variant="ghost"
          size="sm"
          className="w-10 h-10 p-0 text-[#64748B] hover:bg-gray-100 relative"
          onClick={() => document.getElementById('image-upload-input')?.click()}
          title="Upload Image (I)"
          data-testid="tool-image-upload"
        >
          <Image size={20} />
          <input
            id="image-upload-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && onImageUpload) {
                onImageUpload(file);
              }
              e.target.value = ''; // Reset input
            }}
          />
        </Button>

        <div className="w-full h-px bg-gray-200 my-1" />

        {/* Eraser Tool */}
        <Button
          variant={activeTool === 'eraser' ? 'default' : 'ghost'}
          size="sm"
          className={`w-10 h-10 p-0 ${
            activeTool === 'eraser'
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setActiveTool('eraser')}
          title="Eraser (E)"
          data-testid="tool-eraser"
        >
          <Eraser size={20} />
        </Button>

        <div className="w-full h-px bg-gray-200 my-1" />

        {/* Comment Tool */}
        <Button
          variant={activeTool === 'comment' ? 'default' : 'ghost'}
          size="sm"
          className={`w-10 h-10 p-0 relative ${
            activeTool === 'comment'
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setActiveTool('comment')}
          title="Comment (C) - Click on canvas to add comment"
          data-testid="tool-comment"
        >
          <MessageCircle size={20} />
          {/* Comment count badge */}
          {commentCount > 0 && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
              {commentCount > 9 ? '9+' : commentCount}
            </div>
          )}
        </Button>

        {/* Toggle Comments Panel */}
        <Button
          variant="ghost"
          size="sm"
          className="w-10 h-10 p-0 text-[#64748B] hover:bg-gray-100"
          onClick={onToggleCommentsPanel}
          title="View All Comments"
          data-testid="toggle-comments-panel"
        >
          <MessageSquare size={20} />
        </Button>
      </div>

      {/* Bottom Toolbar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white dark:bg-[#1E293B] rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-2 z-40 flex items-center gap-2">
        {/* Undo/Redo */}
        <Button
          variant="ghost"
          size="sm"
          className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100 disabled:opacity-40"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100 disabled:opacity-40"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 size={16} />
        </Button>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* Zoom Controls */}
        <Button
          variant="ghost"
          size="sm"
          className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
          onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
          title="Zoom Out (Ctrl+-)"
        >
          <ZoomOut size={16} />
        </Button>
        <button 
          className="text-sm text-[#64748B] w-14 text-center font-medium hover:bg-gray-100 rounded px-1 py-1"
          onClick={() => setZoom(1)}
          title="Reset Zoom (Ctrl+0)"
        >
          {Math.round(zoom * 100)}%
        </button>
        <Button
          variant="ghost"
          size="sm"
          className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
          onClick={() => setZoom(Math.min(3, zoom + 0.25))}
          title="Zoom In (Ctrl++)"
        >
          <ZoomIn size={16} />
        </Button>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* Grid Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={gridType !== 'none' ? 'default' : 'ghost'}
              size="sm"
              className={`w-8 h-8 p-0 ${
                gridType !== 'none'
                  ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Grid Options"
            >
              <Grid3X3 size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem onClick={() => setGridType('dots')}>
              Dots
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setGridType('grid')}>
              Grid Lines
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setGridType('none')}>
              No Grid
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Snap to Grid */}
        <Button
          variant={snapToGrid ? 'default' : 'ghost'}
          size="sm"
          className={`w-8 h-8 p-0 ${
            snapToGrid
              ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' 
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          onClick={() => setSnapToGrid(!snapToGrid)}
          title="Snap to Grid"
        >
          <Magnet size={16} />
        </Button>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* Templates */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
              title="Templates"
            >
              <Layout size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            <DropdownMenuLabel className="text-xs text-gray-500">Quick Templates</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onApplyTemplate?.('mindmap')}>
              Mind Map
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onApplyTemplate?.('flowchart')}>
              Flowchart
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onApplyTemplate?.('swot')}>
              SWOT Analysis
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onApplyTemplate?.('kanban')}>
              Kanban Board
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onApplyTemplate?.('timeline')}>
              Timeline
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Export */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
              title="Export"
            >
              <Download size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem onClick={() => onExport?.('png')}>
              Export as PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.('svg')}>
              Export as SVG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.('json')}>
              Export as JSON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Present Mode */}
        <Button
          variant="ghost"
          size="sm"
          className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
          onClick={onPresentMode}
          title="Present Mode"
        >
          <Play size={16} />
        </Button>

        {/* Keyboard Shortcuts */}
        <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 text-[#64748B] hover:bg-gray-100"
              title="Keyboard Shortcuts"
            >
              <Keyboard size={16} />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Keyboard Shortcuts</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 mt-4 max-h-80 overflow-y-auto">
              {keyboardShortcuts.map((shortcut, i) => (
                <div key={i} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50">
                  <span className="text-sm text-gray-600">{shortcut.action}</span>
                  <kbd className="px-2 py-0.5 text-xs bg-gray-100 rounded border font-mono">
                    {shortcut.key}
                  </kbd>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
